import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  IconButton,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../auth/AuthContext';
import { tenantFetch } from './api';
import { isSafeRedirectUrl } from './safeRedirect';

declare global {
  interface Window {
    dwolla: {
      configure: (env: 'sandbox' | 'production') => void;
      iav: {
        start: (
          token: string,
          options: {
            container: string;
            stylesheets?: string[];
            microDeposits?: boolean;
            fallbackToMicroDeposits?: boolean;
          },
          callback: (err: Error | null, res?: unknown) => void,
        ) => void;
      };
    };
  }
}

// Shape returned by the backend when PAYMENT_PROVIDER=stripe.
// The Dwolla legacy shape carries `token` + `env`; Stripe carries
// `clientSecret` + `publishableKey`. Branch on the presence of `clientSecret`.
interface StripeSetupPayload {
  clientSecret: string;
  publishableKey: string;
}

type Phase = 'loading' | 'consent' | 'fetching' | 'embedded' | 'redirect' | 'processing' | 'done' | 'error';

export default function TenantSetup() {
  const { user, getIdToken } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('loading');
  const [consented, setConsented] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  // Dwolla IAV state (legacy — kept so app compiles/runs under PAYMENT_PROVIDER=dwolla)
  const [iavToken, setIavToken] = useState<string | null>(null);
  const [iavEnv, setIavEnv] = useState<'sandbox' | 'production'>('sandbox');
  // Stripe Financial Connections state (Spec 037)
  const [stripePayload, setStripePayload] = useState<StripeSetupPayload | null>(null);
  const tokenRef = useRef<string | null>(null);
  // Prevent double-invocation of the Stripe modal in StrictMode
  const stripeRunningRef = useRef(false);

  // Check if already set up; if not, show consent UI
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const token = await getIdToken(false);
      if (!token || cancelled) return;
      tokenRef.current = token;

      const meRes = await tenantFetch('/tenants/me', token);
      if (cancelled) return;
      if (meRes.ok) {
        const meData = await meRes.json();
        if (meData.paymentMethodAttached) {
          navigate('/tenant', { replace: true });
          return;
        }
      }

      if (!cancelled) setPhase('consent');
    })();
    return () => { cancelled = true; };
  }, [user, getIdToken, navigate]);

  // Called when tenant checks the box and clicks "Connect bank account"
  const handleConnect = async () => {
    setPhase('fetching');
    setErrorMsg('');
    try {
      const token = tokenRef.current ?? (await getIdToken(false));
      if (!token) throw new Error('Not signed in');

      const setupRes = await tenantFetch(
        '/tenants/me/payment-method-setup',
        token,
        { method: 'POST', body: JSON.stringify({ consented: true }), headers: { 'Content-Type': 'application/json' } },
      );
      if (!setupRes.ok) {
        const body = await setupRes.json().catch(() => ({}));
        throw new Error((body as any)?.error ?? 'Failed to initialize payment setup.');
      }

      const setupData = await setupRes.json();
      if (setupData.mode === 'redirect') {
        // Defense-in-depth: only follow a backend-supplied URL if it passes the
        // payment-host allowlist (br-k6c). Never blindly trust payload.url.
        if (!isSafeRedirectUrl(setupData.payload?.url)) {
          throw new Error('Invalid payment link');
        }
        window.location.href = setupData.payload.url;
        return;
      }
      if (setupData.mode === 'embedded') {
        const payload = setupData.payload as Record<string, unknown>;
        if (typeof payload.clientSecret === 'string') {
          // Stripe Financial Connections path (Spec 037, PAYMENT_PROVIDER=stripe).
          setStripePayload({
            clientSecret: payload.clientSecret,
            publishableKey: (payload.publishableKey as string) ??
              (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? ''),
          });
          setPhase('embedded');
        } else {
          // Dwolla IAV legacy path (PAYMENT_PROVIDER=dwolla).
          setIavToken(payload.token as string);
          setIavEnv((payload.env as 'sandbox' | 'production') ?? 'sandbox');
          setPhase('embedded');
        }
      }
    } catch (err: any) {
      setErrorMsg(err?.message ?? 'Failed to initialize payment setup. Please try again.');
      setPhase('error');
    }
  };

  // ── Stripe Financial Connections effect ────────────────────────────────────
  // Runs when phase becomes 'embedded' and stripePayload is set.
  // Calls stripe.collectBankAccountForSetup to launch the hosted modal, then
  // confirms the SetupIntent if the bank was linked successfully.
  useEffect(() => {
    if (phase !== 'embedded' || !stripePayload) return;
    if (stripeRunningRef.current) return;
    stripeRunningRef.current = true;

    let cancelled = false;

    (async () => {
      try {
        const stripe = await loadStripe(stripePayload.publishableKey);
        if (!stripe || cancelled) return;

        // We need the tenant's name and email for billing_details.
        // The Firebase user object carries displayName / email.
        const name = (user as any)?.displayName ?? (user as any)?.email ?? 'Tenant';
        const email = (user as any)?.email ?? undefined;

        const result = await stripe.collectBankAccountForSetup({
          clientSecret: stripePayload.clientSecret,
          params: {
            payment_method_type: 'us_bank_account',
            payment_method_data: {
              billing_details: { name, ...(email ? { email } : {}) },
            },
          },
          expand: ['payment_method'],
        });

        if (cancelled) return;

        if (result.error) {
          setErrorMsg(result.error.message ?? 'Bank account link failed. Please try again.');
          setPhase('error');
          return;
        }

        const setupIntent = result.setupIntent;
        if (!setupIntent) {
          setErrorMsg('Unexpected response from payment provider. Please try again.');
          setPhase('error');
          return;
        }

        if (setupIntent.status === 'requires_confirmation') {
          setPhase('processing');
          const confirmResult = await stripe.confirmUsBankAccountSetup(
            stripePayload.clientSecret,
          );
          if (cancelled) return;
          if (confirmResult.error) {
            setErrorMsg(confirmResult.error.message ?? 'Bank account confirmation failed. Please try again.');
            setPhase('error');
            return;
          }
        }

        // Notify backend (best-effort; webhook is authoritative)
        try {
          const tok = tokenRef.current ?? (await getIdToken(false));
          if (tok) {
            await tenantFetch('/tenants/me/payment-method-confirm', tok, { method: 'POST' });
          }
        } catch {
          // non-fatal
        }

        if (!cancelled) navigate('/tenant', { replace: true });
      } catch (err: any) {
        if (!cancelled) {
          setErrorMsg(err?.message ?? 'Payment setup failed. Please try again.');
          setPhase('error');
        }
      } finally {
        stripeRunningRef.current = false;
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, stripePayload]);

  // ── Dwolla IAV effect (legacy) ──────────────────────────────────────────────
  // Runs when phase becomes 'embedded' and iavToken is set (no stripePayload).
  useEffect(() => {
    if (phase !== 'embedded' || !iavToken || stripePayload) return;

    const script = document.createElement('script');
    // SRI hash pins this specific build of dwolla.js. If Dwolla rotates the
    // CDN file the hash will fail and the widget will not load — update the
    // hash in lockstep with Settings.tsx when upgrading Dwolla JS.
    script.src = 'https://cdn.dwolla.com/1/dwolla.js';
    script.integrity = 'sha384-hyo2lOoNXE0qcFn+WHSj/5RZdaxyYxP3ftiz2tP7maynEbDKsNc1lZOthtK9YIOR';
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      window.dwolla.configure(iavEnv);
      window.dwolla.iav.start(
        iavToken,
        { container: 'iavContainer', microDeposits: false, fallbackToMicroDeposits: false },
        (err) => {
          if (err) {
            setErrorMsg('Payment setup failed. Please try again.');
            setPhase('error');
            return;
          }
          setPhase('processing');
          void (async () => {
            try {
              const tok = tokenRef.current ?? (await getIdToken(false));
              if (tok) {
                await tenantFetch('/tenants/me/payment-method-confirm', tok, { method: 'POST' });
              }
            } catch {
              // non-fatal — webhook is authoritative
            }
            navigate('/tenant', { replace: true });
          })();
        },
      );
    };
    script.onerror = () => {
      setErrorMsg('Failed to load payment widget. Please refresh and try again.');
      setPhase('error');
    };
    document.head.appendChild(script);
    return () => {
      if (document.head.contains(script)) document.head.removeChild(script);
      delete (window as any).dwolla;
    };
  }, [phase, iavToken, iavEnv, stripePayload, getIdToken, navigate]);

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Tooltip title="Go back">
            <IconButton
              color="inherit"
              onClick={() => navigate('/tenant')}
              edge="start"
              aria-label="Go back"
              sx={{ mr: 1, opacity: 0.8, '&:hover': { opacity: 1 } }}
            >
              <ArrowBackOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Typography
            variant="h6"
            sx={{ flexGrow: 1, fontFamily: "'Fraunces', serif", fontWeight: 600, letterSpacing: '-0.5px' }}
          >
            Utilibill
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ mt: 4, pb: 8 }}>
        <Typography
          variant="h4"
          sx={{ fontFamily: "'Fraunces', serif", fontWeight: 700, color: 'text.primary', mb: 0.75 }}
        >
          Set up payments
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Connect your bank account to authorize water bill payments.
        </Typography>

        {(phase === 'loading' || phase === 'redirect' || phase === 'processing' || phase === 'fetching') && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {phase === 'error' && (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>
            <Button variant="outlined" onClick={() => { setPhase('consent'); setErrorMsg(''); }}>
              Try again
            </Button>
          </>
        )}

        {phase === 'consent' && (
          <Paper
            elevation={0}
            sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
              By connecting your bank account, you authorize your property manager (via Utilibill)
              to electronically debit your account for the amounts shown on your utility invoices.
              You may revoke this authorization at any time by contacting your property manager.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              Payment processing is provided by a secure third-party payment provider.
              Your bank credentials are never stored by Utilibill.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={consented}
                  onChange={(e) => setConsented(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  I authorize electronic debits from my bank account as described above.
                </Typography>
              }
              sx={{ mb: 2.5, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
            />
            <Button
              variant="contained"
              fullWidth
              disabled={!consented}
              onClick={() => void handleConnect()}
            >
              Connect bank account
            </Button>
          </Paper>
        )}

        {/* Stripe Financial Connections: the modal launches automatically via
            the useEffect above. Show a branded loading state while the modal
            is open or while confirmation is in-flight. */}
        {phase === 'embedded' && stripePayload && (
          <Paper
            elevation={0}
            sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}
          >
            <CircularProgress size={28} sx={{ mb: 2, color: 'primary.main' }} />
            <Typography variant="body2" color="text.secondary">
              Opening bank connection…
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              A secure window will appear to link your bank account.
            </Typography>
          </Paper>
        )}

        {/* Dwolla IAV widget mount point (legacy) */}
        {phase === 'embedded' && !stripePayload && (
          <Paper
            sx={{
              p: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Connect your bank account securely to enable automatic payments.
            </Typography>
            <div id="iavContainer" />
          </Paper>
        )}
      </Container>
    </>
  );
}
